import { Box, Button, CardActions, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useTheme } from '@mui/material';
import { RegenerateForecastOption } from '../../types/api/RegenerateForecastOption';
import React from 'react';

interface GenerateForecastProps {
    cancel: ()=>void,
    validate: (finalOptions: RegenerateForecastOption)=>void,
    generateForecastOption: RegenerateForecastOption
}
export function GenerateForecast(GenerateForecastProps: GenerateForecastProps) {
    const weekDays = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
    const theme = useTheme();

    const [regenerateForecastOption, setRegenerateForecastOption] = React.useState<RegenerateForecastOption>({
        weeklyNoonClientForecast: [...GenerateForecastProps.generateForecastOption.weeklyNoonClientForecast],
        weeklyEveningClientForecast: [...GenerateForecastProps.generateForecastOption.weeklyEveningClientForecast]
    });

    React.useEffect(() => {
        setRegenerateForecastOption({
            weeklyNoonClientForecast: [...GenerateForecastProps.generateForecastOption.weeklyNoonClientForecast],
            weeklyEveningClientForecast: [...GenerateForecastProps.generateForecastOption.weeklyEveningClientForecast]
        });
    }, [GenerateForecastProps.generateForecastOption]);

    const updateForecast = (period: 'noon' | 'evening', index: number, value: string) => {
        const parsedValue = Number.parseInt(value, 10);
        const safeValue = Number.isNaN(parsedValue) ? 0 : Math.max(0, parsedValue);

        setRegenerateForecastOption((prev) => {
            const next: RegenerateForecastOption = {
                weeklyNoonClientForecast: [...prev.weeklyNoonClientForecast],
                weeklyEveningClientForecast: [...prev.weeklyEveningClientForecast]
            };

            if (period === 'noon') {
                next.weeklyNoonClientForecast[index] = safeValue;
            } else {
                next.weeklyEveningClientForecast[index] = safeValue;
            }

            return next;
        });
    };

    return (
        <div>
            <CardContent>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Voulez-vous vraiment régénérer les prévisions pour la semaine actuelle ?
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                    Cette action écrasera les prévisions existantes pour cette semaine.
                </Typography>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                    Prévisions actuelles pour la semaine :
                </Typography>
                <TableContainer sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                    <Table size="small" sx={{ tableLayout: 'auto' }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                                <TableCell sx={{ width: '60px', fontWeight: 'bold', textAlign: 'center' }}></TableCell>
                                {weekDays.map((day) => (
                                    <TableCell key={day} sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: 0.5, py: 1 }}>
                                        {day}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow sx={{ '&:hover': { backgroundColor: theme.palette.action.hover } }}>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '60px', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    Midi
                                </TableCell>
                                {weekDays.map((_, index) => (
                                    <TableCell key={`noon-${index}`} sx={{ textAlign: 'center', px: 0.5, py: 0.75 }}>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={regenerateForecastOption.weeklyNoonClientForecast[index] ?? 0}
                                            onChange={(e) => updateForecast('noon', index, e.target.value)}
                                            sx={{
                                                width: { xs: '50px', sm: '70px', md: '80px' },
                                                '& input': {
                                                    textAlign: 'center',
                                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                    padding: '6px 4px'
                                                }
                                            }}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                            <TableRow sx={{ '&:hover': { backgroundColor: theme.palette.action.hover } }}>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '60px', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    Soir
                                </TableCell>
                                {weekDays.map((_, index) => (
                                    <TableCell key={`evening-${index}`} sx={{ textAlign: 'center', px: 0.5, py: 0.75 }}>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={regenerateForecastOption.weeklyEveningClientForecast[index] ?? 0}
                                            onChange={(e) => updateForecast('evening', index, e.target.value)}
                                            sx={{
                                                width: { xs: '50px', sm: '70px', md: '80px' },
                                                '& input': {
                                                    textAlign: 'center',
                                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                    padding: '6px 4px'
                                                }
                                            }}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                
            </CardContent>
            <CardActions className='justify-between'>
                <Button size="small" color='error' onClick={GenerateForecastProps.cancel}>Non</Button>
                <Button size="small" color='info' onClick={() => GenerateForecastProps.validate(regenerateForecastOption)}>Oui</Button>
            </CardActions>
        </div>
    );
}

export default GenerateForecast;